import React, { useMemo } from 'react';
import { View } from 'react-native';
import { format, formatISO } from 'date-fns';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import Status from 'design-system/Status';
import { rem } from 'design-system/values';
import BlurAmountView from 'design-system/BlurAmountView';
import RadioButton from 'design-system/RadioButton';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import IconExcluded from 'features/subscriptions/icons/IconExcluded';
import { TransactionBankLogo } from 'features/transactions/components/TransactionBankLogo';
import TransactionLogo from 'features/transactions/components/TransactionLogo';
import { selectFeatureFlag } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import PriceChange from './PriceChange';
import HiddenSubscriptionLogo from './HiddenSubscriptionLogo';
import { isWeb } from '../../../../constants';
const RowSubscription = ({ data, isCommitted, isExcluded, selectable, multiSelect, selected, extraText, switchingButton, onPress, blurValues, btnStyles, isNextPaymentRow, hideDuplicateLabel, showLastPayment, backgroundLight, }) => {
    const bankLogoTransaction = useAppSelector((store) => selectFeatureFlag(store, 'bank_logo_transaction')).value;
    const colors = useColors();
    const { priceChange, merchantInfo, customName, currency, predictions, prediction, missedPayment, lastChargeDate, } = data;
    const isPossibleDuplicate = !hideDuplicateLabel && !missedPayment && !!data.duplicateOf?.length;
    // The last charge only when its amounts and date are all known; otherwise
    // the row shows the next predicted payment, so the two never mix.
    const lastPayment = showLastPayment &&
        data.nativePrice.lastPrice != null &&
        data.convertedPrice.lastPrice != null &&
        lastChargeDate
        ? {
            price: data.nativePrice.lastPrice,
            convertedPrice: data.convertedPrice.lastPrice,
            date: lastChargeDate,
        }
        : null;
    const price = Math.abs(lastPayment?.price ?? data.nativePrice.amount);
    const convertedPrice = lastPayment
        ? Math.abs(lastPayment.convertedPrice)
        : data.convertedPrice.amount;
    const lastPaymentDate = lastPayment?.date;
    const displaySign = data.isIncome ? 1 : -1;
    const nextPrediction = predictions && predictions[0] ? predictions[0] : null;
    const rowPressed = () => {
        onPress?.(data);
    };
    const date = useMemo(() => {
        if (lastPaymentDate) {
            return format(formatISO(lastPaymentDate), 'd MMM');
        }
        if (prediction?.date) {
            return format(formatISO(prediction.date), 'd MMM');
        }
        if (nextPrediction) {
            return format(formatISO(nextPrediction.date), 'd MMM');
        }
        return undefined;
    }, [lastPaymentDate, nextPrediction, prediction]);
    const name = customName || merchantInfo?.displayName;
    const styles = useStyles(styleSet);
    return (<TouchableHighlight onCard hoverOnLight style={btnStyles} activeOpacity={1} onPress={rowPressed} disabled={!onPress} underlayColor={colors.background.underlay}>
      <View style={styles.rowContainer}>
        {selectable ? (<RadioButton isChecked={selected} type={multiSelect ? 'multiple' : 'single'} style={styles.radio}/>) : null}
        {blurValues ? (<HiddenSubscriptionLogo />) : (<View>
            <TransactionLogo R44 transaction={data} name={name} {...(backgroundLight
            ? { backgroundLight: true }
            : { backgroundDark: true })}/>
            {bankLogoTransaction ? (<TransactionBankLogo id={data.accountId} isSmall/>) : null}
          </View>)}
        <View style={styles.leftContainer}>
          <BlurAmountView enabled={blurValues}>
            <Text Text-16 {...(isExcluded ? { Gray: true } : { OffBlack: true })} numberOfLines={1} ellipsizeMode="tail">
              {name}
            </Text>
          </BlurAmountView>
          {missedPayment ? (<Status active={false} activeText="Not detected" inactiveText="Not detected"/>) : null}
          {isPossibleDuplicate ? (<Status active={false} activeText="Possible duplicate" inactiveText="Possible duplicate" inactiveVariant="informational"/>) : null}
          {!isNextPaymentRow && !missedPayment && !isPossibleDuplicate && (<BlurAmountView enabled={blurValues} containerStyle={styles.priceChangeBlur}>
              {priceChange && !isCommitted ? (<PriceChange priceChange={priceChange} currency={currency} isIncome={data.isIncome}/>) : null}
            </BlurAmountView>)}
          {extraText ? (<BlurAmountView enabled={blurValues}>
              <Text TextThin-14 Gray>
                {extraText}
              </Text>
            </BlurAmountView>) : null}
          {isExcluded ? (<View style={styles.excluded}>
              <IconExcluded style={styles.excludedIcon}/>
              <Text TextThin-14 Gray>
                Excluded
              </Text>
            </View>) : (switchingButton)}
          <SpaceUserAvatar xxSmall userId={data.userId}/>
        </View>
        <BlurAmountView enabled={blurValues} containerStyle={styles.rightContainer}>
          <Amount Numbers-16 right showSign numberOfLines={1} 
    // eslint-disable-next-line no-nested-ternary
    {...(isExcluded
        ? { Gray: true }
        : data.isIncome
            ? { Blue: true }
            : { OffBlack: true })} value={{ value: displaySign * price, currency }}/>
          {data.nativePrice.currency !== data.convertedPrice.currency && (<Amount NumbersThin-16 Secondary right showSign numberOfLines={1} value={{
                value: displaySign * convertedPrice,
                currency: data.convertedPrice.currency,
            }}/>)}
          {!!date && (<Text TextThin-14 Gray right numberOfLines={1}>
              {date}
            </Text>)}
        </BlurAmountView>
      </View>
    </TouchableHighlight>);
};
const styleSet = createStyleSheets(() => ({
    rowContainer: {
        flexDirection: 'row',
        padding: rem(16),
        alignItems: 'center',
        ...(isWeb && {
            paddingVertical: rem(16),
            paddingHorizontal: rem(8),
        }),
    },
    selectableContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    leftContainer: {
        ...(!isWeb && {
            alignItems: 'flex-start',
        }),
        justifyContent: 'center',
        flex: 1,
        marginLeft: rem(16),
        marginRight: rem(8),
    },
    rightContainer: {
        justifyContent: 'center',
    },
    priceChangeBlur: {
        alignSelf: 'stretch',
    },
    excluded: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    excludedIcon: {
        marginEnd: rem(8),
    },
    radio: {
        marginRight: rem(16),
    },
}));
export default RowSubscription;
